import { LitElement, html, css } from "lit";
import { customElement, state, query } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/dialog/dialog.js";
import "@material/web/icon/icon.js";
import type { MdDialog } from "@material/web/dialog/dialog.js";

import "../components/app-shell";
import { api } from "../lib/api";
import { sharedStyles } from "../styles/shared";
import { inputValue, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { AuthController } from "../lib/auth-controller";
import {
  GI_TYPE_LABELS,
  TRUST_ROLE_LABELS,
  type GiType,
  type GiProduct,
  type GiProducer,
  type GiLicenseStatus,
  type TrustChain,
  type AuthorityGrant,
} from "../lib/types";

// The Provisioning Portal lives in the internal console on the admin.* host
// (host-based serving in cmd/server/main.go), so a root-relative link would
// 404 against this portal's SPA router.
function provisioningPortalUrl(): string {
  const host = location.host.startsWith("dpp.")
    ? "admin." + location.host.slice("dpp.".length)
    : location.host;
  return `${location.protocol}//${host}/provisioning`;
}

const LICENSE_STATUS_LABELS: Record<GiLicenseStatus, string> = {
  active: "Active",
  suspended: "Suspended",
  revoked: "Revoked",
};

// The GI registry console. GI products (PDO/PGI/TSG) and producer
// authorizations are platform-level reference data, so management is gated to
// platform operators — the server enforces it too; we hide controls from other
// users, who see the catalog plus their own organization's authorizations.
@customElement("gi-page")
export class GiPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      form.dialog-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-top: 8px;
        min-width: min(440px, 72vw);
      }
      .header-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
      .grant-form {
        display: flex;
        gap: 12px;
        align-items: center;
        flex-wrap: wrap;
        padding: 0 20px 16px;
      }
      .row-actions {
        display: flex;
        gap: 4px;
        justify-content: flex-end;
      }
      .geo-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        display: inline-block;
      }

      /* EBSI accreditation chain: roundel nodes joined by connectors, with a
         verdict pill below (admin-console design / gi-trust-chain). */
      .chain-pad {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 4px 20px 20px;
      }
      .chain {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        row-gap: 16px;
      }
      .node {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        min-width: 96px;
        text-align: center;
      }
      .node__dot {
        width: 48px;
        height: 48px;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--md-sys-color-primary-container);
        color: var(--md-sys-color-on-primary-container);
        --md-icon-size: 24px;
      }
      .node--bad .node__dot {
        background: var(--md-sys-color-error-container);
        color: var(--md-sys-color-on-error-container);
      }
      .node__role {
        font: 500 12px/1 var(--font-plain, sans-serif);
        letter-spacing: 0.06em;
        color: var(--md-sys-color-primary);
      }
      .node--bad .node__role {
        color: var(--md-sys-color-error);
      }
      .node__name {
        font: 400 12px/1.35 var(--font-plain, sans-serif);
        color: var(--md-sys-color-on-surface-variant);
        max-width: 130px;
        margin-top: 3px;
      }
      .node__issue {
        font-size: 0.6875rem;
        color: var(--md-sys-color-error);
        margin-top: 2px;
        max-width: 130px;
      }
      .link {
        flex: 1;
        display: flex;
        align-items: center;
        min-width: 24px;
        max-width: 64px;
        margin-top: 22px;
        color: var(--md-sys-color-primary);
        --md-icon-size: 16px;
      }
      .link::before {
        content: "";
        flex: 1;
        height: 2px;
        background: var(--md-sys-color-primary);
      }
      .link--bad {
        color: var(--md-sys-color-error);
      }
      .link--bad::before {
        background: repeating-linear-gradient(
          90deg,
          var(--md-sys-color-error) 0 6px,
          transparent 6px 12px
        );
      }
      .verdict {
        align-self: flex-start;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 32px;
        padding: 0 12px 0 8px;
        border-radius: 8px;
        background: var(--md-sys-color-primary-container);
        color: var(--md-sys-color-on-primary-container);
        font: 500 14px/1 var(--font-plain, sans-serif);
        --md-icon-size: 18px;
      }
      .verdict--bad {
        background: var(--md-sys-color-error-container);
        color: var(--md-sys-color-on-error-container);
      }
      .node__did {
        font-family: var(--font-mono, monospace);
        font-size: 0.6875rem;
        color: var(--md-sys-color-on-surface-variant);
        word-break: break-all;
        max-width: 130px;
      }
    `,
  ];

  private auth = new AuthController(this);

  @state() private products: GiProduct[] = [];
  @state() private producers: GiProducer[] = [];
  @state() private chains: Record<string, TrustChain> = {};
  // Designations this caller administers; empty for operators and tenants.
  @state() private grants: AuthorityGrant[] = [];
  @state() private gUser = "";
  @state() private gProduct = "";
  @state() private granting = false;
  @state() private loading = true;
  @state() private error = "";

  // New-product dialog.
  @state() private pName = "";
  @state() private pType: GiType = "PDO";
  @state() private pRegion = "";
  @state() private pCountry = "";
  @state() private pReg = "";
  @state() private pAuthority = "";
  @state() private pTmdn = "";
  @state() private savingProduct = false;
  @state() private productError = "";

  // Authorize-producer dialog.
  @state() private aOrg = "";
  @state() private aProduct = "";
  @state() private aLicense = "";
  @state() private aRegion = "";
  @state() private aValidUntil = "";
  @state() private authorizing = false;
  @state() private authorizeError = "";

  // Status dialog.
  @state() private sProducer?: GiProducer;
  @state() private sStatus: GiLicenseStatus = "suspended";
  @state() private sReason = "";
  @state() private savingStatus = false;
  @state() private statusError = "";

  @query("#product-dialog") private productDialog?: MdDialog;
  @query("#authorize-dialog") private authorizeDialog?: MdDialog;
  @query("#status-dialog") private statusDialog?: MdDialog;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  // Three kinds of caller share this page, and they are not the same thing.
  //
  //   operator  — the platform. Registers designations (an EU-register fact)
  //               and can administer every producer.
  //   authority — a GI consortium. Licenses and enforces producers of the
  //               designations it was granted, and nothing else. It does NOT
  //               register designations.
  //   tenant    — a producer, seeing only its own authorizations.
  private get isOperator(): boolean {
    return this.auth.hasRole("platform_operator");
  }

  private get isAuthority(): boolean {
    return this.auth.hasRole("gi_authority");
  }

  // Only the platform registers or rewrites a designation.
  private get canAuthorDesignations(): boolean {
    return this.isOperator;
  }

  // Licensing and enforcement. The server scopes an authority to its grants, so
  // this stays a coarse "which view do you get" question.
  private get canManageProducers(): boolean {
    return this.isOperator || this.isAuthority;
  }

  // Designations this caller may license into. An operator may use any; an
  // authority only those granted to it, so the authorize dialog cannot offer a
  // choice the server would refuse.
  private get licensableProducts(): GiProduct[] {
    if (this.isOperator) return this.products;
    const granted = new Set(this.grants.map((g) => g.giProductId));
    return this.products.filter((p) => granted.has(p.ID));
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      // Three independent reads, issued together rather than one after the
      // other. Chaining them cost two extra round trips of spinner for calls
      // that never needed each other's results.
      //
      // An authority needs its grants to know what it may work on; an operator
      // needs the whole table to administer it.
      const wantsGrants = this.isAuthority || this.isOperator;
      const [productResp, producerResp] = await Promise.all([
        api.get<{ products: GiProduct[] | null }>("/gi/products"),
        api.get<{ producers: GiProducer[] | null }>(
          this.canManageProducers ? "/gi/producers" : "/gi/producers/my",
        ),
        wantsGrants ? this.loadGrants() : Promise.resolve(),
      ]);
      this.products = productResp.products ?? [];
      this.producers = producerResp.producers ?? [];
      void this.loadChains();
    } catch (err) {
      this.error = errorMessage(err, "Failed to load the GI registry.");
    } finally {
      this.loading = false;
    }
  }

  // Best-effort: resolve each producer's EBSI-style trust chain (RTAO→TAO→TI).
  // A producer with no accreditation simply has no chain (404, ignored).
  private async loadChains(): Promise<void> {
    const entries = await Promise.all(
      this.producers.map(async (p) => {
        try {
          return [p.ID, await api.get<TrustChain>(`/gi/trust-chain/${p.ID}`)] as const;
        } catch {
          return null;
        }
      }),
    );
    const next: Record<string, TrustChain> = {};
    for (const e of entries) if (e) next[e[0]] = e[1];
    this.chains = next;
  }

  private producerCount(productID: string): number {
    return this.producers.filter((p) => p.GiProductID === productID).length;
  }

  private formatDate(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() < 1970) return "—";
    return d.toLocaleDateString();
  }

  private openProduct(): void {
    this.pName = this.pRegion = this.pCountry = "";
    this.pReg = this.pAuthority = this.pTmdn = "";
    this.pType = "PDO";
    this.productError = "";
    this.productDialog?.show();
  }

  private openAuthorize(): void {
    this.aOrg = this.aLicense = this.aRegion = this.aValidUntil = "";
    this.aProduct = this.products[0]?.ID ?? "";
    this.authorizeError = "";
    this.authorizeDialog?.show();
  }

  private openStatus(p: GiProducer): void {
    this.sProducer = p;
    this.sStatus = p.Status === "active" ? "suspended" : "active";
    this.sReason = "";
    this.statusError = "";
    this.statusDialog?.show();
  }

  private async handleCreateProduct(e: Event): Promise<void> {
    e.preventDefault();
    this.productError = "";
    if (!this.pName.trim() || !this.pRegion.trim()) {
      this.productError = "Name and region are required.";
      return;
    }
    this.savingProduct = true;
    try {
      const product = await api.post<GiProduct>("/gi/products", {
        giName: this.pName,
        giType: this.pType,
        region: this.pRegion,
        country: this.pCountry,
        euRegistrationNumber: this.pReg,
        issuingAuthority: this.pAuthority,
        tmdnUrl: this.pTmdn,
      });
      this.products = [product, ...this.products];
      this.productDialog?.close();
      toast(`Registered ${product.GiName}`);
    } catch (err) {
      this.productError = errorMessage(err, "Failed to register GI product.");
    } finally {
      this.savingProduct = false;
    }
  }

  private async handleAuthorize(e: Event): Promise<void> {
    e.preventDefault();
    this.authorizeError = "";
    if (!this.aOrg.trim() || !this.aProduct || !this.aLicense.trim()) {
      this.authorizeError = "Organization, product and license number are required.";
      return;
    }
    this.authorizing = true;
    try {
      const producer = await api.post<GiProducer>("/gi/producers", {
        organizationId: this.aOrg.trim(),
        giProductId: this.aProduct,
        licenseNumber: this.aLicense.trim(),
        authorizedRegion: this.aRegion,
        validUntil: this.aValidUntil
          ? new Date(this.aValidUntil).toISOString()
          : undefined,
      });
      this.producers = [producer, ...this.producers];
      this.authorizeDialog?.close();
      toast(`Authorized ${producer.OrgName || producer.OrgID}`);
    } catch (err) {
      this.authorizeError = errorMessage(err, "Failed to authorize producer.");
    } finally {
      this.authorizing = false;
    }
  }

  private async handleStatus(e: Event): Promise<void> {
    e.preventDefault();
    this.statusError = "";
    if (!this.sProducer || !this.sReason.trim()) {
      this.statusError = "A reason is required.";
      return;
    }
    this.savingStatus = true;
    try {
      const updated = await api.put<GiProducer>(
        `/gi/producers/${this.sProducer.ID}/status`,
        { status: this.sStatus, reason: this.sReason.trim() },
      );
      this.producers = this.producers.map((p) =>
        p.ID === updated.ID ? updated : p,
      );
      this.statusDialog?.close();
      toast(`Producer ${LICENSE_STATUS_LABELS[updated.Status].toLowerCase()}`);
    } catch (err) {
      this.statusError = errorMessage(err, "Failed to change status.");
    } finally {
      this.savingStatus = false;
    }
  }

  private renderProducts() {
    if (this.products.length === 0) {
      return html`<div class="card empty">
        No GI products registered yet.${this.canAuthorDesignations
          ? " Register one from the EU GIview / TMDN registry."
          : ""}
      </div>`;
    }
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>GI products <small>· ${this.products.length}</small></h2>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>GI name</th>
              <th>Scheme</th>
              <th>Region</th>
              <th>Producers</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${this.products.map(
              (p) => html`
                <tr>
                  <td>
                    <b>${p.GiName}</b>
                    ${p.TmdnURL
                      ? html`<small
                          ><a href=${p.TmdnURL} target="_blank" rel="noopener"
                            >GIview ↗</a
                          ></small
                        >`
                      : null}
                  </td>
                  <td><span class="badge badge--gi">${p.GiType}</span></td>
                  <td>${p.Region}${p.Country ? html`, ${p.Country}` : null}</td>
                  <td>${this.producerCount(p.ID)}</td>
                  <td>
                    ${p.Status === "active"
                      ? html`<span class="badge badge--ok"
                          >${icon("verified")} Verified</span
                        >`
                      : html`<span class="badge badge--neutral"
                          >${icon("schedule")} ${p.Status}</span
                        >`}
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  private geoBadge(p: GiProducer) {
    const color =
      p.GeocodingStatus === "verified"
        ? "var(--md-sys-color-primary)"
        : p.GeocodingStatus === "failed"
          ? "var(--md-sys-color-error)"
          : "var(--md-sys-color-outline)";
    return html`<span class="badge badge--neutral"
      ><span class="geo-dot" style="background:${color}"></span
      >${p.GeocodingStatus}</span
    >`;
  }

  private renderProducers() {
    if (this.producers.length === 0) {
      return html`<div class="card empty">
        ${this.canManageProducers
          ? "No producers authorized yet."
          : "Your organization has no GI authorizations yet."}
      </div>`;
    }
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>
            ${this.canManageProducers ? "Authorized producers" : "My GI authorizations"}
            <small>· ${this.producers.length}</small>
          </h2>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>${this.canManageProducers ? "Organization" : "GI product"}</th>
              <th>License</th>
              <th>Region</th>
              <th>Location</th>
              <th>Status</th>
              ${this.canManageProducers ? html`<th></th>` : null}
            </tr>
          </thead>
          <tbody>
            ${this.producers.map(
              (p) => html`
                <tr>
                  <td>
                    <b
                      >${this.canManageProducers
                        ? p.OrgName || p.OrgID
                        : p.GiProductName}</b
                    >
                    ${this.canManageProducers
                      ? html`<small>${p.GiProductName}</small>`
                      : null}
                  </td>
                  <td><code>${p.LicenseNumber}</code></td>
                  <td>${p.AuthorizedRegion || "—"}</td>
                  <td>${this.geoBadge(p)}</td>
                  <td>
                    ${p.Status === "active"
                      ? html`<span class="badge badge--ok"
                          >${icon("verified")}
                          ${LICENSE_STATUS_LABELS[p.Status]}</span
                        >`
                      : p.Status === "revoked"
                        ? html`<span class="badge badge--bad"
                            >${icon("flag")}
                            ${LICENSE_STATUS_LABELS[p.Status]}</span
                          >`
                        : html`<span class="badge badge--neutral"
                            >${icon("schedule")}
                            ${LICENSE_STATUS_LABELS[p.Status]}</span
                          >`}
                  </td>
                  ${this.canManageProducers
                    ? html`<td>
                        <div class="row-actions">
                          <md-text-button @click=${() => this.openStatus(p)}>
                            Manage
                          </md-text-button>
                        </div>
                      </td>`
                    : null}
                </tr>
              `,
            )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  // Renders each resolved trust chain as RTAO → TAO → TI roundel nodes joined
  // by connectors, with a verdict pill — the EBSI Verifiable Accreditation
  // chain that authorizes GI provenance (admin-console design).
  // Who administers which designation. Operator-only: a consortium that could
  // grant itself scope would make the boundary decorative, so this is the one
  // place the mapping is created — and it is why the gi_authority role confers
  // nothing on its own.
  private renderAuthorityGrants() {
    if (!this.isOperator) return null;
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>GI authorities</h2>
          <p class="muted">
            A consortium administers the producers of the designations granted
            here — licensing, suspension and renewal — and can reach no others.
          </p>
        </div>
        <div class="grant-form">
          <md-outlined-text-field
            label="User id"
            .value=${this.gUser}
            @input=${(e: Event) => (this.gUser = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-select
            label="Designation"
            .value=${this.gProduct}
            @change=${(e: Event) => (this.gProduct = inputValue(e))}
          >
            ${this.products.map(
              (p) => html`<md-select-option value=${p.ID}>
                <div slot="headline">${p.GiName}</div>
              </md-select-option>`,
            )}
          </md-outlined-select>
          <md-filled-button
            ?disabled=${this.granting || !this.gUser || !this.gProduct}
            @click=${() => void this.grant()}
          >
            Grant
          </md-filled-button>
        </div>
        ${this.grants.length === 0
          ? html`<div class="card empty">No authorities registered yet.</div>`
          : html`<div class="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Designation</th>
                    <th>User</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${this.grants.map(
                    (g) => html`
                      <tr>
                        <td>${g.giName || g.giProductId}</td>
                        <td><code>${g.userId}</code></td>
                        <td>
                          <div class="row-actions">
                            <md-text-button
                              @click=${() => void this.revoke(g)}
                            >
                              Revoke
                            </md-text-button>
                          </div>
                        </td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table>
            </div>`}
      </div>
    `;
  }

  private async grant(): Promise<void> {
    this.granting = true;
    try {
      await api.post("/gi/authority-admins", {
        userId: this.gUser,
        giProductId: this.gProduct,
      });
      this.gUser = "";
      this.gProduct = "";
      await this.loadGrants();
      toast("Authority granted.");
    } catch (err) {
      toast(errorMessage(err, "Could not grant authority."), true);
    } finally {
      this.granting = false;
    }
  }

  private async revoke(g: AuthorityGrant): Promise<void> {
    try {
      await api.del(
        `/gi/authority-admins?userId=${encodeURIComponent(g.userId)}&giProductId=${encodeURIComponent(g.giProductId)}`,
      );
      await this.loadGrants();
      toast("Authority revoked — the consortium loses access immediately.");
    } catch (err) {
      toast(errorMessage(err, "Could not revoke authority."), true);
    }
  }

  private async loadGrants(): Promise<void> {
    try {
      const resp = await api.get<{ grants: AuthorityGrant[] | null }>(
        "/gi/authority-admins",
      );
      this.grants = resp.grants ?? [];
    } catch {
      this.grants = [];
    }
  }

  private renderTrustChains() {
    const chains = this.producers
      .map((p) => this.chains[p.ID])
      .filter((c): c is TrustChain => !!c && c.links.length > 0);
    if (chains.length === 0) return null;
    const roleIcon = (i: number) =>
      ["account_balance", "workspace_premium", "factory"][Math.min(i, 2)];
    return html`
      ${chains.map(
        (c) => html`
          <div class="card card--flush">
            <div class="cardhead">
              <h2>
                EBSI accreditation chain
                <small>· ${c.giProductName || "GI product"}</small>
              </h2>
              ${c.trusted
                ? html`<span class="badge badge--ok"
                    >${icon("verified")} Chain trusted</span
                  >`
                : html`<span class="badge badge--bad"
                    >${icon("gpp_bad")} Chain broken</span
                  >`}
            </div>
            <div class="chain-pad">
              <div class="chain">
                ${c.links.map(
                  (l, i) => html`
                    ${i > 0
                      ? html`<span
                          class="link ${l.valid ? "" : "link--bad"}"
                          >${icon(l.valid ? "arrow_forward" : "link_off")}</span
                        >`
                      : null}
                    <div class="node ${l.valid ? "" : "node--bad"}">
                      <span class="node__dot"
                        >${icon(l.valid ? roleIcon(i) : "link_off")}</span
                      >
                      <div>
                        <div class="node__role">${l.role}</div>
                        <div class="node__name">
                          ${l.name || TRUST_ROLE_LABELS[l.role] || "—"}
                        </div>
                        ${l.did
                          ? html`<div class="node__did">${l.did}</div>`
                          : null}
                        ${!l.valid
                          ? html`<div class="node__issue">
                              ${l.issue || l.status}
                            </div>`
                          : null}
                      </div>
                    </div>
                  `,
                )}
              </div>
              ${c.trusted
                ? html`<span class="verdict"
                    >${icon("verified_user")} Chain trusted — accreditation
                    verified via EBSI</span
                  >`
                : html`<span class="verdict verdict--bad"
                    >${icon("gpp_bad")} Chain broken —
                    ${c.broken || "accreditation cannot be verified"}</span
                  >`}
            </div>
          </div>
        `,
      )}
    `;
  }

  render() {
    return html`
      <app-shell active="/gi">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>GI registry</h1>
              <p>
                Protected Geographical Indications (PDO / PGI / TSG) and the
                producers authorized to make them.
                ${this.isOperator
                  ? html`Source and convert producers in the
                      <a href=${provisioningPortalUrl()}>Provisioning Portal</a
                      >.`
                  : this.isAuthority
                    ? html`You administer
                        ${this.grants.length === 1
                          ? this.grants[0].giName
                          : `${this.grants.length} designations`}
                        — licensing and enforcement for those producers is
                        yours; the designations themselves are maintained by the
                        platform from the EU register.`
                    : html`Producer authorizations are granted by your GI
                      authority.`}
              </p>
            </div>
            ${this.canManageProducers
              ? html`
                  <div class="header-actions">
                    ${this.canAuthorDesignations
                      ? html`<md-text-button @click=${() => this.openProduct()}>
                          <span slot="icon">${icon("add")}</span>
                          New GI product
                        </md-text-button>`
                      : null}
                    <md-filled-button
                      @click=${() => this.openAuthorize()}
                      ?disabled=${this.licensableProducts.length === 0}
                    >
                      <span slot="icon">${icon("workspace_premium")}</span>
                      Authorize producer
                    </md-filled-button>
                  </div>
                `
              : null}
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : html`
                  ${this.renderTrustChains()} ${this.renderProducts()}
                  ${this.renderProducers()} ${this.renderAuthorityGrants()}
                `}
        </div>

        ${this.renderProductDialog()} ${this.renderAuthorizeDialog()}
        ${this.renderStatusDialog()}
      </app-shell>
    `;
  }

  private renderProductDialog() {
    return html`
      <md-dialog id="product-dialog">
        <div slot="headline">Register GI product</div>
        <form
          slot="content"
          id="product-form"
          class="dialog-form"
          @submit=${(e: Event) => void this.handleCreateProduct(e)}
        >
          <md-outlined-text-field
            label="GI name"
            required
            placeholder="Champagne"
            .value=${this.pName}
            @input=${(e: Event) => (this.pName = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-select
            label="Type"
            .value=${this.pType}
            @change=${(e: Event) => (this.pType = inputValue(e) as GiType)}
          >
            ${(Object.keys(GI_TYPE_LABELS) as GiType[]).map(
              (t) => html`
                <md-select-option value=${t}>
                  <div slot="headline">${GI_TYPE_LABELS[t]}</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>
          <md-outlined-text-field
            label="Protected region"
            required
            placeholder="Champagne"
            .value=${this.pRegion}
            @input=${(e: Event) => (this.pRegion = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Country"
            placeholder="France"
            .value=${this.pCountry}
            @input=${(e: Event) => (this.pCountry = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="EU registration number"
            .value=${this.pReg}
            @input=${(e: Event) => (this.pReg = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Issuing authority"
            placeholder="INAO"
            .value=${this.pAuthority}
            @input=${(e: Event) => (this.pAuthority = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="TMDN GIview URL"
            .value=${this.pTmdn}
            @input=${(e: Event) => (this.pTmdn = inputValue(e))}
          ></md-outlined-text-field>
          ${this.productError
            ? html`<p class="error-text">${this.productError}</p>`
            : null}
        </form>
        <div slot="actions">
          <md-text-button @click=${() => this.productDialog?.close()}>
            Cancel
          </md-text-button>
          <md-filled-button
            form="product-form"
            type="submit"
            ?disabled=${this.savingProduct}
          >
            ${this.savingProduct ? "Saving…" : "Register"}
          </md-filled-button>
        </div>
      </md-dialog>
    `;
  }

  private renderAuthorizeDialog() {
    return html`
      <md-dialog id="authorize-dialog">
        <div slot="headline">Authorize producer</div>
        <form
          slot="content"
          id="authorize-form"
          class="dialog-form"
          @submit=${(e: Event) => void this.handleAuthorize(e)}
        >
          <md-outlined-select
            label="GI product"
            required
            .value=${this.aProduct}
            @change=${(e: Event) => (this.aProduct = inputValue(e))}
          >
            ${this.licensableProducts.map(
              (p) => html`
                <md-select-option value=${p.ID}>
                  <div slot="headline">${p.GiName} (${p.GiType})</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>
          <md-outlined-text-field
            label="Organization ID"
            required
            supporting-text="The tenancy organization being authorized"
            .value=${this.aOrg}
            @input=${(e: Event) => (this.aOrg = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="License number"
            required
            placeholder="CH-2024-001"
            .value=${this.aLicense}
            @input=${(e: Event) => (this.aLicense = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Authorized sub-region"
            .value=${this.aRegion}
            @input=${(e: Event) => (this.aRegion = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Valid until"
            type="date"
            .value=${this.aValidUntil}
            @input=${(e: Event) => (this.aValidUntil = inputValue(e))}
          ></md-outlined-text-field>
          ${this.authorizeError
            ? html`<p class="error-text">${this.authorizeError}</p>`
            : null}
        </form>
        <div slot="actions">
          <md-text-button @click=${() => this.authorizeDialog?.close()}>
            Cancel
          </md-text-button>
          <md-filled-button
            form="authorize-form"
            type="submit"
            ?disabled=${this.authorizing}
          >
            ${this.authorizing ? "Authorizing…" : "Authorize"}
          </md-filled-button>
        </div>
      </md-dialog>
    `;
  }

  private renderStatusDialog() {
    return html`
      <md-dialog id="status-dialog">
        <div slot="headline">Manage license</div>
        <form
          slot="content"
          id="status-form"
          class="dialog-form"
          @submit=${(e: Event) => void this.handleStatus(e)}
        >
          ${this.sProducer
            ? html`<p>
                ${this.sProducer.OrgName || this.sProducer.OrgID} ·
                ${this.sProducer.GiProductName}
              </p>`
            : null}
          <md-outlined-select
            label="New status"
            .value=${this.sStatus}
            @change=${(e: Event) =>
              (this.sStatus = inputValue(e) as GiLicenseStatus)}
          >
            <md-select-option value="active"
              ><div slot="headline">Active</div></md-select-option
            >
            <md-select-option value="suspended"
              ><div slot="headline">Suspended</div></md-select-option
            >
            <md-select-option value="revoked"
              ><div slot="headline">Revoked</div></md-select-option
            >
          </md-outlined-select>
          <md-outlined-text-field
            label="Reason"
            required
            .value=${this.sReason}
            @input=${(e: Event) => (this.sReason = inputValue(e))}
          ></md-outlined-text-field>
          ${this.statusError
            ? html`<p class="error-text">${this.statusError}</p>`
            : null}
        </form>
        <div slot="actions">
          <md-text-button @click=${() => this.statusDialog?.close()}>
            Cancel
          </md-text-button>
          <md-filled-button
            form="status-form"
            type="submit"
            ?disabled=${this.savingStatus}
          >
            ${this.savingStatus ? "Saving…" : "Apply"}
          </md-filled-button>
        </div>
      </md-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "gi-page": GiPage;
  }
}
